import type { ReactNode } from "react";

export interface SpokeSection {
  id: string;
  heading: string;
  body: ReactNode;
  /** Plain-text bold conclusion rendered at the end of the section. */
  conclusion: string;
}

export interface SpokeFAQ {
  q: string;
  a: string;
}

export interface SpokeContent {
  age: 50 | 55 | 60;
  slug: string;
  title: string; // page H1
  metaTitle: string;
  metaDescription: string;
  heroLede: string;
  /** Three plain-text key conclusions shown right under the calculator. */
  keyConclusions: string[];
  sections: SpokeSection[];
  faqs: SpokeFAQ[];
}

const p = (s: ReactNode) => (
  <p className="text-base text-foreground/80 leading-relaxed">{s}</p>
);

/* ───────────────────────── AGE 50 ───────────────────────── */

export const RETIRE_AT_50: SpokeContent = {
  age: 50,
  slug: "how-much-to-retire-at-50",
  title: "How Much Do You Need to Retire at 50?",
  metaTitle:
    "How Much Money Do You Need to Retire at 50? (2026)",
  metaDescription:
    "How much money do you need to retire at 50? About 28–33× yearly spending for a 40-year retirement. See worked numbers, healthcare costs and a calculator.",
  heroLede:
    "Retiring at 50 is the hardest version of early retirement: you have the shortest accumulation window and the longest drawdown. Here are the real numbers — no fluff.",
  keyConclusions: [
    "On €2,500/month of spending (€30,000/yr), you need roughly €750,000 invested at 50 using the standard 25× rule — and closer to €900,000–€990,000 if you want a 33× safety buffer for a 40-year drawdown.",
    "Plan an extra €600–€1,400/month per adult for private health cover between age 50 and Medicare/state-pension age — that alone pushes the target by €180,000–€420,000.",
    "Starting from €0 at age 30, retiring at 50 requires a gross savings rate near 55–65% at a 7% real return. Starting at 25 drops that to roughly 40–45%.",
  ],
  sections: [
    {
      id: "the-25x-rule-at-50",
      heading: "Why the 25× rule isn't quite enough at 50",
      body: (
        <>
          {p(
            "The 4% rule (and its inverse, the 25× rule) was originally back-tested by William Bengen and the Trinity Study on a 30-year retirement. Retire at 50 and you may need the money to last 40–45 years — long enough that historically safe withdrawal rates drop to around 3.0–3.5%.",
          )}
          {p(
            "In practice that means using 28× to 33× annual expenses as your target instead of the textbook 25×. On €30,000/yr of spending that's €840,000–€990,000 rather than €750,000. Model this in the calculator above by either raising monthly expenses by ~15–20% or, equivalently, dragging the target nest-egg field up directly.",
          )}
        </>
      ),
      conclusion:
        "For a 40-year retirement at 50, target 28×–33× annual expenses — roughly €840K–€990K on €30K/yr of spending.",
    },
    {
      id: "healthcare-gap-50-to-65",
      heading: "Bridging the healthcare gap from 50 to 65",
      body: (
        <>
          {p(
            "If you retire at 50 in the United States, Medicare is 15 years away. Unsubsidized ACA premiums for a 50-year-old typically run $800–$1,500/month, climbing each year — and a couple can easily face $20,000–$30,000/year in combined premiums plus out-of-pocket costs.",
          )}
          {p(
            "In Europe the equivalent is voluntary private cover or topping up state cover after losing employer-linked benefits. Realistic budgets: €400–€800/month per adult in Germany (PKV), €100–€300/month in France (mutuelle), £200–£400/month in the UK for private cover. Add this number to the calculator's monthly-expenses field — it's the single biggest line item most people miss.",
          )}
        </>
      ),
      conclusion:
        "Add €600–€1,400/month per adult for healthcare between 50 and 65. That single line raises your nest-egg target by €180,000–€420,000.",
    },
    {
      id: "early-withdrawal-strategies",
      heading: "Getting at your money before 59½ without penalties",
      body: (
        <>
          {p(
            "401(k) and traditional IRA withdrawals before 59½ trigger a 10% penalty on top of income tax. At 50 you've got nearly a decade to bridge. Three practical levers:",
          )}
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>
              <strong>Rule 72(t) / SEPP:</strong> Substantially Equal Periodic
              Payments let you draw a fixed actuarial amount from an IRA
              penalty-free, but you must continue for 5 years or until 59½ —
              whichever is longer. Mess up the schedule and the IRS retroactively
              applies all penalties.
            </li>
            <li>
              <strong>Roth conversion ladder:</strong> Convert a year of expenses
              from traditional to Roth every year. After a 5-year season each
              converted amount can be withdrawn penalty-free. Start the ladder
              the year you retire so the first rungs are ready at 55.
            </li>
            <li>
              <strong>Taxable brokerage bridge:</strong> The simplest path.
              Build a 5–10 year expense buffer in a taxable account; long-term
              capital gains at 0% or 15% are usually cheaper than the penalty.
            </li>
          </ul>
        </>
      ),
      conclusion:
        "Plan to hold 5–10 years of expenses (€150K–€300K on €30K/yr) outside tax-advantaged accounts so you can bridge from 50 to 59½ without the 10% early-withdrawal penalty.",
    },
    {
      id: "sequence-of-returns",
      heading: "Sequence-of-returns risk: why the first 5 years matter most",
      body: (
        <>
          {p(
            "Two retirees can have identical average returns over 40 years and end up with wildly different outcomes — the one who hit a bad market in years 1–5 can run out of money decades earlier. This is sequence-of-returns risk, and it's the single biggest danger of retiring at 50.",
          )}
          {p(
            "Standard mitigations: hold 2–3 years of expenses in cash or a very low-risk account (or low-volatility ETFs), glide your equity ETF exposure down around the retirement date (say 50–60% in lower-volatility / dividend ETFs at 50, easing back to 70–80% equity ETFs by 65), and pre-commit to spending cuts of 5–10% in any year your portfolio drops more than 20%.",
          )}
        </>
      ),
      conclusion:
        "Hold 2–3 years of expenses (€60K–€90K) in cash or a very low-risk account at 50, and pre-commit to a 5–10% spending cut after any 20%+ market drop.",
    },
    {
      id: "savings-rate-reality",
      heading: "The savings rate it actually takes",
      body: (
        <>
          {p(
            "At a 7% real return, the math is unforgiving. Starting from €0 at 30 and retiring at 50 (20 years of compounding) requires saving roughly 55–65% of gross income to hit a €750K target. Start at 25 (25 years) and that drops to about 40–45%. Start at 35 and it's closer to 80% — usually only feasible with a partner or a high-income year on top.",
          )}
          {p(
            "Use the calculator above to back-solve your exact number. Adjust current age, current savings and target — the 'Save €X/mo' figure is the answer for your numbers.",
          )}
        </>
      ),
      conclusion:
        "Starting from zero at 30, plan on saving 55–65% of gross income to retire at 50. Starting at 25 cuts that to roughly 40–45%.",
    },
    {
      id: "coast-fire-at-50",
      heading: "Coast FIRE: when you can stop saving and still retire at 50",
      body: (
        <>
          {p(
            "Coast FIRE is the point where your existing portfolio, growing at the assumed real return without any new contributions, hits your nest-egg target by your chosen retirement age. To coast to €750K by 50 at 7% real, you'd need roughly €280K invested by 30, €390K by 35, or €545K by 40.",
          )}
          {p(
            "After you cross that line you can drop to a lower-paying job, take a sabbatical, or shift to part-time work — as long as you can cover current expenses from earned income, the retirement portfolio takes care of itself.",
          )}
        </>
      ),
      conclusion:
        "Coast FIRE checkpoints to retire at 50 on €750K (7% real, no further contributions): ≈€280K by 30, ≈€390K by 35, ≈€545K by 40.",
    },
  ],
  faqs: [
    {
      q: "How much do I need to retire at 50?",
      a: "Using the 4% rule, multiply your expected annual retirement expenses by 25. On €30,000/year of spending that is €750,000 invested. Because a retirement starting at 50 may need to last 40–45 years, most planners raise the multiplier to 28×–33×, giving a target range of €840,000–€990,000.",
    },
    {
      q: "Is €1 million enough to retire at 50?",
      a: "Roughly, if your spending stays at or below €30,000–€35,000/year. €1,000,000 at a 3% safe withdrawal rate (the rate planners use for a 40-year horizon) supports €30,000/year of withdrawals; at 3.5% it supports €35,000/year. Healthcare costs come out of the same pot.",
    },
    {
      q: "How much should I save per month to retire at 50?",
      a: "Starting from €0 at age 30 and aiming for a €750,000 nest egg by 50 (20 years at a 7% real return) requires saving roughly €1,450/month. Starting from €50,000 at 30 drops the required contribution to about €1,000/month.",
    },
    {
      q: "What is the 4% rule, and does it work at 50?",
      a: "The 4% rule says you can withdraw 4% of your portfolio in year one of retirement and adjust for inflation each year. It was tested for 30-year retirements; for a 40-year drawdown at 50, the safe rate falls to roughly 3.0–3.5%, which is why early retirees target 28×–33× annual expenses instead of 25×.",
    },
    {
      q: "How do I bridge healthcare from 50 to 65?",
      a: "Plan €600–€1,400/month per adult for private cover (US ACA, German PKV, UK private). Over 15 years that is €108,000–€252,000 per adult — directly addable to your nest-egg target as roughly €180,000–€420,000 of extra savings needed.",
    },
    {
      q: "Can I access my 401(k) or IRA at 50?",
      a: "Not without a 10% penalty until 59½. Three legal bypasses: Rule 72(t) SEPP withdrawals, the Roth conversion ladder (5-year season), and a taxable brokerage bridge of 5–10 years of expenses (€150,000–€300,000 on €30,000/year of spending).",
    },
  ],
};

/* ───────────────────────── AGE 55 ───────────────────────── */

export const RETIRE_AT_55: SpokeContent = {
  age: 55,
  slug: "how-much-to-retire-at-55",
  title: "How Much Do You Need to Retire at 55?",
  metaTitle:
    "How Much Do I Need to Retire at 55? Real Numbers",
  metaDescription:
    "How much do you need to retire at 55? Roughly 25–28× yearly spending, plus a healthcare bridge to 65. See worked numbers, the Rule of 55 and a calculator.",
  heroLede:
    "Retiring at 55 is the sweet spot of early retirement — long enough horizon to need a buffer, short enough that the Rule of 55 unlocks penalty-free 401(k) withdrawals. Here are the numbers.",
  keyConclusions: [
    "On €2,500/month of spending (€30,000/yr), you need roughly €750,000 invested to retire at 55 using the classic 4% rule — and €840,000 if you want a 28× safety buffer for a 30–35 year drawdown.",
    "The IRS Rule of 55 lets you withdraw from the 401(k) of the employer you separated from at age 55 or later, penalty-free. It does not cover IRAs or earlier employers' plans.",
    "Plan €600–€1,200/month per adult for private health cover between age 55 and 65 — adding roughly €100,000–€200,000 to your nest-egg target.",
  ],
  sections: [
    {
      id: "the-25x-rule-at-55",
      heading: "The 25× rule at 55 — and when to nudge it to 28×",
      body: (
        <>
          {p(
            "The 4% rule says you can safely withdraw 4% of your portfolio in year one of retirement and adjust for inflation each year after. Flip it: save 25 times your annual expenses. On €30,000/yr of spending that's a €750,000 target.",
          )}
          {p(
            "Retire at 55 and your money may need to last 35–40 years rather than the 30 years Bengen tested. Historically that drops the safe withdrawal rate to about 3.5%, which means a 28× target — €840,000 on €30,000/yr. Use the calculator above to test both: enter your target age as 55 and either bump monthly expenses up ~12% or drag the nest-egg target field directly.",
          )}
        </>
      ),
      conclusion:
        "Target 25× annual expenses for a baseline retirement at 55, or 28× (€840K on €30K/yr of spending) for a 35-year-plus drawdown buffer.",
    },
    {
      id: "rule-of-55",
      heading: "The Rule of 55: penalty-free 401(k) access (US)",
      body: (
        <>
          {p(
            "The IRS Rule of 55 is a quiet superpower for US early retirees. If you separate from your employer in the calendar year you turn 55 or later, you can withdraw from that employer's 401(k) without the 10% early-withdrawal penalty. You still pay ordinary income tax, but the penalty is gone.",
          )}
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>
              It only covers the 401(k) of the employer you just left. Old
              401(k)s from prior jobs and all IRAs are excluded.
            </li>
            <li>
              Consolidate before you leave: roll older 401(k)s into your current
              employer's plan (if it accepts roll-ins) so the whole balance
              qualifies.
            </li>
            <li>
              Do <em>not</em> roll the 401(k) to an IRA after separation if you
              plan to use the Rule of 55 — once it's an IRA, the penalty
              reappears until 59½.
            </li>
            <li>
              Public safety workers get the same treatment at age 50.
            </li>
          </ul>
        </>
      ),
      conclusion:
        "If you leave your employer at 55+ and keep the money in that 401(k), you can withdraw penalty-free for the 4.5 years until 59½ — saving you 10% versus an IRA.",
    },
    {
      id: "eu-early-pension",
      heading: "Early pension access in Europe at 55",
      body: (
        <>
          {p(
            "Europe doesn't have the Rule of 55, but most countries let you access pillar-2 and pillar-3 pensions before state-pension age — usually with a permanent reduction:",
          )}
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>
              <strong>Germany:</strong> Riester and Rürup can pay from 60–62 in
              most contracts. State pension early-claim before 67 typically
              costs 0.3%/month (~3.6%/yr) for life.
            </li>
            <li>
              <strong>France:</strong> Retraite anticipée pour carrière longue
              from 58–60 if you started working before 20. Otherwise the legal
              age is 64 with reductions before full rate.
            </li>
            <li>
              <strong>Netherlands:</strong> Most pillar-2 schemes allow
              retirement from 55, with the payout actuarially reduced — often
              30–40% lower than waiting to AOW age.
            </li>
            <li>
              <strong>UK:</strong> Personal pensions and SIPPs are accessible
              from 55 (rising to 57 in April 2028). 25% tax-free lump sum,
              the rest taxed as income.
            </li>
          </ul>
        </>
      ),
      conclusion:
        "Most EU pillar-2/3 pensions open at 55–60 with a 5–8%/year permanent reduction for early claims — plan to live mainly off taxable savings until those payments justify themselves.",
    },
    {
      id: "healthcare-bridge-55-65",
      heading: "Healthcare from 55 to 65",
      body: (
        <>
          {p(
            "Ten years of private cover is the bridge nobody plans for. Real-world monthly premiums per adult, no children:",
          )}
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>US ACA (silver plan, no subsidy):</strong> $900–$1,600/month at 55–64.</li>
            <li><strong>Germany (PKV after 55):</strong> €600–€1,100/month.</li>
            <li><strong>UK private (Bupa/AXA):</strong> £250–£500/month.</li>
            <li><strong>Spain/Portugal:</strong> €80–€200/month — significantly cheaper for active expats.</li>
          </ul>
          {p(
            "Take your number, add it to monthly expenses in the calculator, and watch the nest-egg target update. €1,000/month of extra expenses lifts the target by €300,000.",
          )}
        </>
      ),
      conclusion:
        "Budget €600–€1,200/month per adult for healthcare from 55 to 65 — €72,000–€144,000 over the decade, or €100,000–€200,000 in extra nest-egg target.",
    },
    {
      id: "catch-up-contributions",
      heading: "Catch-up contributions: the last 5 years matter a lot",
      body: (
        <>
          {p(
            "From age 50 the US lets you bolt extra contributions onto every tax-advantaged account: an extra $7,500/yr to 401(k)s and $1,000/yr to IRAs (2026 limits). From 60–63 the SECURE 2.0 'super catch-up' bumps the 401(k) extra to roughly $11,250.",
          )}
          {p(
            "Run the math: maxing the catch-ups from 50–55 is $42,500 of extra principal over 5 years. At 7% real returns that compounds to about $50,000–$55,000 by 55 — and meaningfully more if you keep working to 60.",
          )}
        </>
      ),
      conclusion:
        "Maxing the 50+ catch-up contributions for the 5 years before 55 adds roughly $50,000 to your retirement balance — directly subtractable from your gap.",
    },
  ],
  faqs: [
    {
      q: "How much do I need to retire at 55?",
      a: "Using the 4% rule, you need 25× your expected annual retirement expenses saved. On €30,000/year of spending that is €750,000 invested. For a 35-year-plus drawdown most planners step the multiplier up to 28×, which is €840,000 on the same €30,000/year.",
    },
    {
      q: "Is €1 million enough to retire at 55?",
      a: "Yes, if annual spending is around €35,000–€40,000. €1,000,000 at a 3.5–4% safe withdrawal rate supports €35,000–€40,000/year for 35 years, including healthcare. Above €45,000/year you will want closer to €1.2M.",
    },
    {
      q: "How much should I save per month to retire at 55?",
      a: "From €0 at age 35 to a €750,000 target by 55 (20 years at a 7% real return) requires roughly €1,450/month. From €100,000 at 35 it drops to about €700/month.",
    },
    {
      q: "What is the Rule of 55?",
      a: "An IRS rule letting you withdraw from the 401(k) of the employer you left at age 55 or later without the 10% early-withdrawal penalty. You still pay ordinary income tax. It does not cover IRAs or earlier employers' 401(k)s — saving you 4.5 years × 10% in penalties versus an IRA.",
    },
    {
      q: "Can I use the Rule of 55 in Europe?",
      a: "No — it is US IRS only. The EU equivalent is early access to pillar-2/3 pensions from 55–60, almost always with an actuarial reduction of about 0.3% per month claimed early (3.6%/year), or roughly 14.4% for life if claimed 4 years before full pension age.",
    },
    {
      q: "How much should I have saved by 55 if I plan to keep working to 65?",
      a: "Roughly €450,000–€550,000 by 55. At a 7% real return, €500,000 with €1,000/month added grows to about €1.15M by 65 — comfortably above the €750,000 25× target on €30,000/year of spending.",
    },
  ],
};

/* ───────────────────────── AGE 60 ───────────────────────── */

export const RETIRE_AT_60: SpokeContent = {
  age: 60,
  slug: "how-much-to-retire-at-60",
  title: "How Much Do You Need to Retire at 60?",
  metaTitle:
    "How Much Do I Need to Retire at 60? Real Numbers",
  metaDescription:
    "Retiring at 60 needs ~25× annual expenses, a 5-year healthcare bridge and a Social Security plan. See exact numbers and a live calculator.",
  heroLede:
    "Retiring at 60 is the easiest version of early retirement — penalty-free IRA access in 18 months, Social Security two years later, and a drawdown horizon the 4% rule was actually built for.",
  keyConclusions: [
    "On €2,500/month of spending (€30,000/yr), you need roughly €750,000 invested to retire at 60 — the classic 25× target works at 60 because the drawdown horizon is 25–30 years.",
    "Claiming US Social Security at 62 cuts your benefit by ~30%; delaying to 70 boosts it by 24% above Full Retirement Age. The breakeven between claiming early and at FRA (67) is usually age 80–82.",
    "A €1,800/month Social Security or state-pension stream from 67 cuts the nest egg you need at 60 by roughly €500,000 — because you only need to self-fund 7 years of full spending instead of 30.",
  ],
  sections: [
    {
      id: "the-25x-rule-at-60",
      heading: "Why 25× works (and sometimes 22×) at 60",
      body: (
        <>
          {p(
            "At 60, the 4% rule is on solid ground. Bengen's original safe withdrawal research assumed a 30-year retirement — exactly the horizon you face if you retire at 60 and plan for 90. On €30,000/yr of spending the 25× target is €750,000.",
          )}
          {p(
            "Once you layer in expected Social Security or a state pension starting at 65–67, you only need to self-fund the gap years and a smaller residual. Many planners use 22–23× of pre-pension expenses for retirees at 60 with substantial expected benefits — that drops the target to €660,000–€690,000.",
          )}
        </>
      ),
      conclusion:
        "Plan on 25× annual expenses (€750K on €30K/yr) at 60. With a meaningful state pension or Social Security from 67, you can responsibly drop to 22–23× — about €660K–€690K.",
    },
    {
      id: "social-security-optimization",
      heading: "Social Security: claim at 62, 67, or 70?",
      body: (
        <>
          {p(
            "For anyone born in 1960 or later, Full Retirement Age (FRA) is 67. Claim early at 62 and your benefit is permanently reduced to about 70% of your FRA amount. Delay past FRA and you earn 8% per year in delayed retirement credits up to age 70 — a 24% lifetime boost.",
          )}
          {p(
            "Worked example: a €2,000/month FRA benefit becomes about €1,400/month at 62 or €2,480/month at 70. The cumulative breakeven between claiming at 62 and at 67 is usually age 78–82; between 67 and 70 it's age 82–84. If you have above-average longevity, a healthy spouse, or no other inflation-linked income, delaying is almost always the higher expected-value play.",
          )}
          {p(
            "Tactically: retire at 60, live off the portfolio from 60 to 70, then start the maximum-delayed Social Security check. This is sometimes called 'Social Security as longevity insurance' — you spend down the portfolio in the years you're most likely to be alive, and lean on the inflation-linked government check in the years that pose the highest sequence and longevity risk.",
          )}
        </>
      ),
      conclusion:
        "Claiming Social Security at 70 instead of 62 boosts your monthly check by ~76% for life. For a €2,000 FRA benefit, that's €1,400/mo vs €2,480/mo — a difference of €13,000/yr forever.",
    },
    {
      id: "eu-state-pension-at-60",
      heading: "European state pensions if you retire at 60",
      body: (
        <>
          {p(
            "Most EU state pensions can't be drawn at 60 without large permanent reductions:",
          )}
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Germany:</strong> Standard retirement age 67. Early claim from 63 (with 35 contribution years) at -0.3%/month = -14.4% lifetime.</li>
            <li><strong>France:</strong> Legal age 64 (62 for long-career workers). Below full-rate age you lose ~5%/yr (decote).</li>
            <li><strong>Netherlands:</strong> AOW age tied to life expectancy, currently 67. No early option.</li>
            <li><strong>UK:</strong> State pension at 66 (rising to 67 by 2028, 68 by 2046). No early claim.</li>
          </ul>
          {p(
            "Plan the 60–67 window to be fully self-funded. That's 7 years × €30,000 = €210,000 of expense coverage that has to come entirely from your portfolio before the state pension shows up.",
          )}
        </>
      ),
      conclusion:
        "EU retirees at 60 must self-fund roughly 5–7 years (€150K–€210K on €30K/yr) before the state pension begins — and early-claim penalties of 5–8%/yr usually make it cheaper to wait.",
    },
    {
      id: "penalty-free-access-at-595",
      heading: "Penalty-free IRA and 401(k) access at 59½",
      body: (
        <>
          {p(
            "Retire at 60 and you've already cleared the 10% early-withdrawal penalty on every traditional IRA and 401(k). You still pay ordinary income tax on traditional-account withdrawals, but the penalty bridge problem that dominates planning at 50 and 55 simply doesn't exist.",
          )}
          {p(
            "That changes the optimal account structure. Many 60-year-old retirees should hold less in taxable brokerage than someone retiring at 50 — taxable money sitting there earns less tax-advantaged growth than the same money inside a Roth.",
          )}
        </>
      ),
      conclusion:
        "At 60, every dollar in your IRA and 401(k) is reachable without the 10% penalty — you only need a 1–2 year cash buffer rather than the 5–10 year bridge required at 50.",
    },
    {
      id: "roth-conversion-window",
      heading: "The Roth conversion sweet spot: ages 60 to 73",
      body: (
        <>
          {p(
            "The years between retiring and the first Required Minimum Distribution (currently age 73, rising to 75 in 2033) are usually your lowest taxable-income years for life. No salary, no RMDs yet, possibly delaying Social Security — your tax bracket can be near zero.",
          )}
          {p(
            "That's the time to convert traditional 401(k) and IRA balances into Roth — paying tax at 10–12% (US) instead of the 22–24% bracket your RMDs will likely push you into later. A typical strategy: convert each year exactly up to the top of the 12% bracket (about $94,000 of taxable income for a US couple in 2026).",
          )}
        </>
      ),
      conclusion:
        "Each €50,000 you Roth-convert in the low-bracket 60–73 window can save €5,000–€8,000 in lifetime tax versus letting RMDs force the withdrawal at a higher bracket.",
    },
    {
      id: "rmds-and-the-60-73-window",
      heading: "Why RMD planning starts at 60, not 73",
      body: (
        <>
          {p(
            "Required Minimum Distributions force you to withdraw a percentage of your traditional balances starting at 73. The first-year RMD is about 3.8% of the balance and rises every year — a $1.5M traditional IRA throws off a $57,000 mandatory withdrawal in year one, taxed as ordinary income.",
          )}
          {p(
            "If you don't act between 60 and 73, you may be forced into a higher tax bracket every year of your 70s and 80s. Roth conversions, qualified charitable distributions (QCDs), and front-loading traditional withdrawals in the 60s all reduce the eventual RMD base.",
          )}
        </>
      ),
      conclusion:
        "Without planning, a $1.5M traditional IRA at 73 forces a $57K/yr taxable withdrawal — using the 60–73 window to convert or front-load can cut lifetime tax by tens of thousands.",
    },
  ],
  faqs: [
    {
      q: "How much do I need to retire at 60?",
      a: "Using the 4% rule, you need 25× your expected annual retirement expenses. On €30,000/year of spending that is €750,000. Layering in a state pension or Social Security from 67 lets responsible planners drop to 22–23× pre-pension expenses (€660,000–€690,000).",
    },
    {
      q: "Is €750,000 enough to retire at 60?",
      a: "Yes, on roughly €30,000/year of spending. €750,000 × 4% = €30,000/year of safe withdrawals for a 30-year retirement starting at 60 — the horizon the 4% rule was originally back-tested on.",
    },
    {
      q: "Can I retire at 60 with €500,000?",
      a: "Tight, but possible on €18,000–€20,000/year of spending plus a future state pension. €500,000 × 4% = €20,000/year; a €1,200/month state pension starting at 67 then lifts total income to €34,400/year.",
    },
    {
      q: "Should I take Social Security at 62 or wait?",
      a: "Claiming at 62 permanently cuts your benefit ~30% versus Full Retirement Age (67), and ~43% versus age 70. A €2,000/month FRA benefit becomes €1,400 at 62 or €2,480 at 70. Cumulative breakeven between 62 and 67 lands around age 78–82.",
    },
    {
      q: "How much do I save each month from age 50 to retire at 60?",
      a: "From €300,000 at 50, hitting €750,000 by 60 (10 years at 7% real) requires about €1,500/month of additional contributions. From €500,000 at 50, it drops to roughly €350/month.",
    },
    {
      q: "What is a Roth conversion ladder, and why does it matter at 60?",
      a: "The 60-to-73 window (before RMDs start) is usually your lowest taxable-income years. Converting traditional IRA balances to Roth at 10–12% tax can save 10–12 percentage points per dollar versus the 22–24% bracket RMDs typically force later — on €50,000 converted, that is €5,000–€8,000 of lifetime tax saved.",
    },
  ],
};

export const SPOKE_CONTENT = {
  50: RETIRE_AT_50,
  55: RETIRE_AT_55,
  60: RETIRE_AT_60,
} as const;
